import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import useMeasure from 'react-use-measure';
import React, { useContext } from 'react';
import { pxToRem } from 'src/common/utils/fonts';

const SliderComponent = Slider;

const SliderContainer = styled(Box)(() => ({
  width: '100%',
  height: '100%',

  '& > .slick-slider': {
    height: '100%',
  },

  '.slick-list': {
    height: '100%',
  },

  '.slick-track': {
    height: '100%',
  },

  '.slick-slide > div': {
    height: '100%',
  },

  '.slick-prev:before, .slick-next:before': {
    display: 'none',
  },
}));

const ArrowBtn = styled('button')(({ theme }) => {
  return {
    padding: theme.spacing(10),
    width: 'fit-content',
    height: 'fit-content',
    borderRadius: '50%',
    zIndex: 2,

    '& > svg': {
      color: theme.palette.grey[600],
    },

    '&:hover': {
      background: 'rgba(0,0,0,0.06)',
    },
  };
});

interface SlickArrowProps {
  className?: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  side: 'left' | 'right';
}

const ArrowButton = ({ className, onClick, side }: SlickArrowProps) => {
  const Icon = side === 'left' ? ArrowBackIosNewIcon : ArrowForwardIosIcon;

  return (
    <ArrowBtn className={className} style={{ [side]: pxToRem(-10) }} onClick={onClick}>
      <Icon sx={{ color: 'black', fontSize: '1rem' }} />
    </ArrowBtn>
  );
};

const settings = {
  variableWidth: true,
  arrows: true,
  infinite: true,
  slidesToShow: 1,
  slidesToScroll: 1,
  nextArrow: <ArrowButton side="right" />,
  prevArrow: <ArrowButton side="left" />,
};

export const arrowButtonsWidth = 20;

type CaruselItemProps = {
  children: React.ReactNode;
  width: number;
};

const CaruselContext = React.createContext<{ containerWidthWithoutArrows: number }>({
  containerWidthWithoutArrows: 0,
});

export const CaruselItem = ({ children, width }: CaruselItemProps) => {
  const { containerWidthWithoutArrows } = useContext(CaruselContext);

  return (
    <div
      style={{
        width: `${width <= 1 ? width * containerWidthWithoutArrows : width}px`,
        height: '100%',
        marginRight: '0.625rem',
      }}
    >
      {children}
    </div>
  );
};

type CaruselProps = {
  children: React.ReactNode;
};

export const Carusel = ({ children }: CaruselProps) => {
  const [root, bounds] = useMeasure();
  const adjustedContainerWidth = bounds.width - arrowButtonsWidth * 2;

  return (
    <Box ref={root} sx={{ height: '16rem' }}>
      <CaruselContext.Provider value={{ containerWidthWithoutArrows: adjustedContainerWidth }}>
        <SliderContainer className="slider-container">
          <SliderComponent {...settings}>{children}</SliderComponent>
        </SliderContainer>
      </CaruselContext.Provider>
    </Box>
  );
};
